import React, {useState, useEffect} from 'react'
import {Grid, Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {AMENITY_TYPE_OPERATIONAL, AMENITY_TYPE_RECREATIONAL} from '../../constants/ModelConstants'
import {useSelector, useDispatch} from 'react-redux'
import Routes from '../../constants/RouteConstants'
import {matchPath, useHistory, useLocation} from 'react-router-dom'
import AmenityConstants from './AmenitiesConstants'
import SelectInput from '../SelectInput'
import {ONBOARDING_AMENITY_KEY_PICKUP_LABEL} from '../../constants/ModelConstants'
import DiscardChangesDialog from '../DiscardChangesDialog'
import AmenityForm from './AmenityForm'
import ElevatorForm from './ElevatorForm'
import {RXRButton} from '../RXRButton'
import {
  createNewAmenity,
  updateAmenity,
  getAmenityDetails,
  deleteClosure,
  deleteGeneralAvailability,
  getAmenityWithLinks,
  createBidirectionalAmenityLink,
  deleteBidirectionalAmenityLink,
} from '../../lib/queries'
import Loader from '../Loader'
import useFormChanged, {NonEmptyValidator, NonEmptyArrayValidator, NumberValidator} from '../hooks/useFormChanged'
import FileHelper from '../../lib/FileHelper'
import {Typography as TypographyStyle} from './../../assets/styles'
const ELEVATOR_AMENITY = 'elevator'
const KEY_PICKUP_AMENITY = 'key-pickup'
import {loadAmenitiesForBuilding} from '../../actions/amenitiesActions'
import {rxrRedColor} from '../../assets/styles/color'
import {selectActiveBuildingAndSisterBuildingIds} from '../../reducers/selectors'

const useStyles = makeStyles(theme => ({
  formContainer: {
    maxWidth: theme.breakpoints.values.lg,
  },
  cancel: {
    fontWeight: 'bold',
    fontStyle: 'normal',
    fontSize: 16,
    color: '#6E3C2C',
    alignItems: 'center',
    textAlign: 'center',
  },
  cursorHand: {
    cursor: 'pointer',
  },
  headerTitle: {
    ...TypographyStyle.H3Header,
    lineHeight: '22px',
    marginTop: '14px',
    marginBottom: '30px',

    fontSize: 40,
    color: '#431A1A',
    fontWeight: 'bold',
    fontStyle: 'normal',
  },
  requiredFieldsErrorMessage: {
    marginBottom: 16,
  },
}))

const RESERVATION_INCREMENT = 15

const EMPTY_STATE = {
  buildingId: null,
  label: '',
  description: '',
  rules: '',
  needsApproval: false,
  attachments: [],
  isVisible: true,
  advancedNoticeCancel: 0,
  advancedNoticeRequest: 0,
  minReservationDuration: 15,
  maxReservationDuration: 180,
  mustAgreeToRules: false,
  ignoreBookInAdvanceForStaff: false,
  reservationPadding: 0,
  maxPartySize: 1,
  maxOccupancy: 1,
  maxConcurrentReservations: 1,
  weeklyReservationLimit: 0,
  reservationIncrement: RESERVATION_INCREMENT,
  generalAvailability: [],
  closures: [],
  requiresPayment: false,
  hourlyPrice: 0,
  depositPrice: 0,
  amenityType: '',
}

function CreateAmenityPage(props) {
  const classes = useStyles()
  let amenityType = props.amenityType

  const [formTypeState, setFormTypeState] = useState(ELEVATOR_AMENITY)

  useEffect(() => {
    if (amenityType === AMENITY_TYPE_OPERATIONAL && formTypeState === KEY_PICKUP_AMENITY) {
      if (form.label !== ONBOARDING_AMENITY_KEY_PICKUP_LABEL) {
        setForm({...form, label: ONBOARDING_AMENITY_KEY_PICKUP_LABEL})
      }
    }
  }, [formTypeState, amenityType])

  const activeBuildingId = useSelector(state => state.Buildings.activeBuildingId)
  const buildingIds = useSelector(selectActiveBuildingAndSisterBuildingIds)
  const amenitiesLookup = useSelector(state => state.Amenities.amenitiesLookup)
  const dispatch = useDispatch()
  const history = useHistory()
  const {form, setForm, formChanged, resetInitialForm, invalidItems, validateForm} = useFormChanged(
    EMPTY_STATE,
    amenityType === AMENITY_TYPE_RECREATIONAL
      ? useFormChanged.PropLevelValidation({
          label: NonEmptyValidator,
          description: NonEmptyValidator,
          attachments: NonEmptyArrayValidator,
          advancedNoticeCancel: NumberValidator,
          advancedNoticeRequest: NumberValidator,
          reservationPadding: NumberValidator,
          maxPartySize: NumberValidator,
          maxOccupancy: NumberValidator,
          weeklyReservationLimit: NumberValidator,
          maxConcurrentReservations: NumberValidator,
          generalAvailability: NonEmptyArrayValidator,
          amenityType: NonEmptyValidator,
        })
      : useFormChanged.PropLevelValidation({
          label: NonEmptyValidator,
          description: NonEmptyValidator,
          generalAvailability: NonEmptyArrayValidator,
        }),
  )

  const currentLocation = useLocation()
  const [editingAmenityId, setEditingAmenityId] = useState(null)
  const [isLoadingAmenityDetails, setIsLoadingAmenityDetails] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // these will store lists of IDs that need to be deleted on save
  const [availabilitiesToRemove, setAvailabilitiesToRemove] = useState([])
  const [closuresToRemove, setClosuresToRemove] = useState([])

  // Amenity linking state
  const [linkedAmenities, setLinkedAmenities] = useState([])
  const [originalLinkedAmenities, setOriginalLinkedAmenities] = useState([]) // Track original for comparison
  const [isLoadingAmenities, setIsLoadingAmenities] = useState(false)

  useEffect(() => {
    const editMatch =
      matchPath(currentLocation.pathname, {path: Routes.AMENITIES_EDIT_OPERATIONAL}) ||
      matchPath(currentLocation.pathname, {path: Routes.AMENITIES_EDIT_RECREATIONAL})

    if (!props.isCreate && editMatch && editMatch.params.amenityId !== editingAmenityId) {
      setEditingAmenityId(editMatch.params.amenityId)
    } else {
      setEditingAmenityId(null)
    }
  }, [currentLocation, props.isCreate])

  useEffect(() => {
    if (editingAmenityId) {
      setIsLoadingAmenityDetails(true)
      getAmenityDetails(editingAmenityId)
        .then(amenityDetails => {
          const updatedAmenityDetails = {
            ...amenityDetails,
            requiresPayment: amenityDetails.requiresPayment || false,
            hourlyPrice: amenityDetails.hourlyPrice || 0,
            depositPrice: amenityDetails.depositPrice || 0,
          }
          setForm(updatedAmenityDetails)
          setIsLoadingAmenityDetails(false)
        })
        .catch(err => {
          window.alert(err.message)
        })
    }
  }, [editingAmenityId])

  // Load available amenities for linking
  useEffect(() => {
    if (buildingIds) {
      setIsLoadingAmenities(true)
      loadAmenitiesForBuilding(dispatch, buildingIds).finally(() => setIsLoadingAmenities(false))
    }
  }, [buildingIds])

  // Load existing links when editing an amenity
  useEffect(() => {
    if (editingAmenityId) {
      getAmenityWithLinks(editingAmenityId)
        .then(amenityWithLinks => {
          if (!amenityWithLinks) {
            return
          }

          // Transform child amenities
          const childLinks = (amenityWithLinks.childAmenities || []).map(item => {
            return {
              id: item.id,
              amenityId: item.childAmenity.id,
              relationship: 'child',
              parentAmenityId: editingAmenityId,
              childAmenityId: item.childAmenity.id,
            }
          })

          // Transform parent amenities
          const parentLinks = (amenityWithLinks.parentAmenities || []).map(item => {
            return {
              id: item.id,
              amenityId: item.parentAmenity.id,
              relationship: 'parent',
              parentAmenityId: item.parentAmenity.id,
              childAmenityId: editingAmenityId,
            }
          })
          setLinkedAmenities([...childLinks, ...parentLinks])
          setOriginalLinkedAmenities([...childLinks, ...parentLinks])
        })
        .catch(err => {
          console.error('Error loading amenity links:', err)
        })
    } else {
      setLinkedAmenities([])
      setOriginalLinkedAmenities([])
    }
  }, [editingAmenityId])

  const updateForm = newFormVals => {
    setForm({...form, ...newFormVals})
  }

  const handleRemoveGeneralAvailability = gA => {
    if (gA.id) {
      setAvailabilitiesToRemove([...availabilitiesToRemove, gA.id])
    }
  }

  const handleRemoveClosure = c => {
    if (c.id) {
      setClosuresToRemove([...closuresToRemove, c.id])
    }
  }

  const handleLinksChange = newLinks => {
    setLinkedAmenities(newLinks)
  }

  const saveAmenityHandler = async () => {
    if (!validateForm()) {
      return
    }
    setIsSaving(true)

    let input = {
      buildingId: activeBuildingId,
      label: form.label,
      description: form.description,
      rules: form.rules,
      mustAgreeToRules: form.mustAgreeToRules,
      ignoreBookInAdvanceForStaff: form.ignoreBookInAdvanceForStaff,
      needsApproval: form.needsApproval,
      attachments: form.attachments.map(a => FileHelper.formatS3ObjectForInput(a)),
      advancedNoticeCancel: form.advancedNoticeCancel,
      advancedNoticeRequest: form.advancedNoticeRequest,
      minReservationDuration: form.minReservationDuration,
      maxReservationDuration: form.maxReservationDuration,
      maxPartySize: form.maxPartySize,
      maxOccupancy: form.maxOccupancy,
      maxConcurrentReservations: form.maxConcurrentReservations,
      weeklyReservationLimit: form.weeklyReservationLimit,
      reservationPadding: form.reservationPadding,
      reservationIncrement: RESERVATION_INCREMENT,
      isVisible: form.isVisible,
      isOnboardingOnly: amenityType === AMENITY_TYPE_OPERATIONAL,
      requiresPayment: form.requiresPayment,
      hourlyPrice: form.hourlyPrice,
      depositPrice: form.depositPrice,
      amenityType: form.amenityType,
    }

    try {
      let savedAmenityId = editingAmenityId

      if (editingAmenityId) {
        await updateAmenity({...input, id: editingAmenityId}, form.closures, form.generalAvailability)
      } else {
        const result = await createNewAmenity(input, form.closures, form.generalAvailability)
        savedAmenityId = result.id
      }

      if (closuresToRemove.length > 0) {
        await Promise.all(closuresToRemove.map(deleteClosure))
      }

      if (availabilitiesToRemove.length > 0) {
        await Promise.all(availabilitiesToRemove.map(deleteGeneralAvailability))
      }

      // Handle amenity links
      if (savedAmenityId) {
        // Find links to create (in current but not in original)
        const currentCompleteLinks = linkedAmenities.filter(link => link.amenityId && link.relationship)
        const originalCompleteLinks = originalLinkedAmenities.filter(link => link.amenityId && link.relationship)

        const linksToCreate = currentCompleteLinks.filter(current => {
          return !originalCompleteLinks.some(
            original => original.amenityId === current.amenityId && original.relationship === current.relationship,
          )
        })

        // Find links to delete (in original but not in current)
        const linksToDelete = originalCompleteLinks.filter(original => {
          return !currentCompleteLinks.some(
            current => current.amenityId === original.amenityId && current.relationship === original.relationship,
          )
        })

        // Create new links
        for (const link of linksToCreate) {
          // If relationship is 'parent', the linked amenity is the parent and current amenity is the child
          // If relationship is 'child', the current amenity is the parent and linked amenity is the child
          const currentAmenityIsParent = link.relationship === 'child'
          await createBidirectionalAmenityLink(savedAmenityId, link.amenityId, currentAmenityIsParent)
        }

        // Delete removed links
        for (const link of linksToDelete) {
          await deleteBidirectionalAmenityLink(savedAmenityId, link.amenityId)
        }
      }

      resetInitialForm(EMPTY_STATE, () => {
        setLinkedAmenities([])
        setOriginalLinkedAmenities([])
        history.push(
          amenityType === AMENITY_TYPE_OPERATIONAL ? Routes.OPERATIONAL_AMENITIES_MANAGER : Routes.RECREATIONAL_AMENITIES_MANAGER,
        )
      })
    } catch (er) {
      console.error(er)
      window.alert('Sorry, we could not process this request. Please re-enter the amenity information.')
    }
    setIsSaving(false)
  }

  return isLoadingAmenityDetails || isSaving ? (
    <Loader />
  ) : (
    <React.Fragment>
      <div>
        <div style={{margin: '24px'}}>
          <DiscardChangesDialog hasChanges={formChanged} />
          <div className={classes.formContainer}>
            <Grid container spacing={3} className={classes.formRow}>
              <Grid item>
                <Typography className={classes.headerTitle}>{!editingAmenityId ? 'New amenity' : form.label}</Typography>
              </Grid>
            </Grid>
            {amenityType === 'operational' && (
              <Grid container spacing={3}>
                <Grid item md={3}>
                  <SelectInput
                    label={'Amenity type'}
                    onChange={v => setFormTypeState(v)}
                    options={[
                      {label: 'Elevator', value: ELEVATOR_AMENITY},
                      {
                        label: ONBOARDING_AMENITY_KEY_PICKUP_LABEL,
                        value: KEY_PICKUP_AMENITY,
                        isDisabled: Object.values(amenitiesLookup || {}).some(
                          a => a.isOnboardingOnly && a.label === ONBOARDING_AMENITY_KEY_PICKUP_LABEL,
                        ),
                      },
                    ]}
                    value={formTypeState}
                    isRequired={true}
                    disabled={!!editingAmenityId}
                  />
                </Grid>
              </Grid>
            )}
            {amenityType === AMENITY_TYPE_RECREATIONAL ? (
              <AmenityForm
                mode={editingAmenityId ? 'editAmenity' : 'newAmenity'}
                onRemoveGeneralAvailability={handleRemoveGeneralAvailability}
                onRemoveClosure={handleRemoveClosure}
                updateForm={updateForm}
                amenityId={editingAmenityId}
                linkedAmenities={linkedAmenities}
                onLinksChange={handleLinksChange}
                isSaving={isSaving}
                {...form}
                invalidItems={invalidItems}
              />
            ) : (
              <ElevatorForm
                mode={editingAmenityId ? 'editAmenity' : 'newAmenity'}
                onRemoveGeneralAvailability={handleRemoveGeneralAvailability}
                onRemoveClosure={handleRemoveClosure}
                updateForm={updateForm}
                {...form}
                invalidItems={invalidItems}
                labelDisabled={amenityType === AMENITY_TYPE_OPERATIONAL && formTypeState === KEY_PICKUP_AMENITY}
              />
            )}
          </div>
          {invalidItems.length > 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} className={classes.requiredFieldsErrorMessage}>
                <Typography color={rxrRedColor}>Form items are incomplete above. Please fill out all required fields.</Typography>
              </Grid>
            </Grid>
          )}
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <RXRButton onClick={() => saveAmenityHandler()}>{AmenityConstants.BTN_SAVE}</RXRButton>
            </Grid>
            <Grid item className={classes.cursorHand}>
              <Typography className={classes.cancel} onClick={() => history.push(Routes.RECREATIONAL_AMENITIES_MANAGER)}>
                {AmenityConstants.BTN_CANCEL}
              </Typography>
            </Grid>
          </Grid>
        </div>
      </div>
    </React.Fragment>
  )
}

export default CreateAmenityPage
